import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
    useNavigate,
    useNavigation,
    useActionData,
    useLoaderData,
    useSubmit,
    redirect,
    useBlocker,
    useLocation,
} from "react-router";
import {
    Alert,
    Form,
    FloatingLabel,
    Modal,
    Button,
    Row,
    Col,
    Image,
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import styled from "styled-components";
import CreatableSelect from "react-select/creatable";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faCircleLeft } from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "components/wishlist/Breadcrumb";
import {
    renderSavingProgressToast,
    renderSimpleToast,
} from "components/shared/Toasts";
import { WishlistItemDetailSchema } from "schema/wishlist";
import imagePlaceHolder from "assets/images/product_image_placeholder.png";
import { useLocalStorage } from "utils/LocalStorage";
import { errorCode } from "../exception/ErrorCode";
import { useTheme } from "components/shared/ThemeProvider";

export async function loader({ params }) {
    const wishlist = await axios
        .get(`wishlistitem/${params.wishlistRequestId}`)
        .then((response) => response.data);

    // replace null with empty string if image url is null
    // it is because hidden HTML input element assigned with a null value is not allowed
    wishlist.image_url = wishlist.image_url ?? "";
    return wishlist;
}

export async function action({ request }) {
    const updates = WishlistItemDetailSchema.cast(await request.json(), {
        stripUnknown: true,
    });
    let id = updates["id"];
    let request_config = null;
    if (request.method === "PUT") {
        // update item
        request_config = {
            method: "put",
            url: `wishlistitem/${id}`,
            data: updates,
        };
    } else if (request.method === "POST") {
        // create item
        request_config = {
            method: "post",
            url: "wishlistitem/",
            data: updates,
        };
    } else if (request.method === "DELETE") {
        // delete item
        request_config = {
            method: "delete",
            url: `wishlistitem/${id}`,
        };
    } else {
        throw new Error("Invalid request method", request.method);
    }

    const result = await renderSavingProgressToast(axios(request_config))
        .then((response) => ({ success: true, id: response.data["id"] ?? id }))
        .catch((error) => {
            if (error.response?.data) {
                var data = error.response?.data;
                // partial of the errors are handled gracefully, otherwise propagate to root error handler
                if (
                    [
                        errorCode.maxItemsLimitReached,
                        errorCode.permissionDenied,
                        errorCode.duplicatedData,
                    ].findIndex((code) => code === data["error_code"]) > -1
                )
                    return Promise.resolve({
                        success: false,
                        code: data["error_code"],
                        reason: data["description"],
                    });
            }
            return Promise.reject(error);
        });

    if (result.success === true) {
        if (request.method === "DELETE") {
            return redirect("/wishlist");
        }
        return redirect(`/wishlist/${result.id}`);
    }

    return result;
}

const ProductImage = styled(Image)`
    width: 100%;
`;

const ImageContainer = styled.div`
    position: relative;
    cursor: pointer;
`;

const ImageOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    line-height: 1.4;
`;

const OverlayText = styled.div`
    max-width: 200px;
`;

const ImageUrlModal = ({ image_url, modalShow, onHide, onImageUrlChange }) => {
    const image_schema = object({
        image_url: string().url().nullable().label("Image Url").ensure(),
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(image_schema),
        values: { image_url },
    });

    const onSubmit = async (data) => {
        onImageUrlChange(data.image_url);
        onHide();
    };

    const onError = (errors, e) => console.error(errors, e);

    return (
        <Modal centered backdrop={true} show={modalShow} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Edit Image Url</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit(onSubmit, onError)}>
                    <Controller
                        control={control}
                        name="image_url"
                        render={({ field: { onChange, value, ref } }) => (
                            <FloatingLabel
                                className="mb-3"
                                controlId="image_url"
                                label="Image Url"
                            >
                                <Form.Control
                                    type="url"
                                    isInvalid={errors.image_url}
                                    onChange={onChange}
                                    value={value}
                                    data-testid="modal_input_image_url"
                                    ref={ref}
                                    placeholder="Image Url"
                                    name="image_url"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.image_url?.message}
                                </Form.Control.Feedback>
                            </FloatingLabel>
                        )}
                    />
                    <div className="d-flex gap-2">
                        <Button variant="secondary" onClick={onHide}>
                            Close
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            data-testid="modal_btn_submit"
                        >
                            Save Changes
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

const EditDetailForm = () => {
    const request = useLoaderData() || {
        id: 0,
        image_url: "",
        product_name: "",
        product_url: "",
        tags: [],
    };
    const actionData = useActionData();
    const [record, setRecord] = useState(request);
    const [modalShow, setModalShow] = useState(false);
    const nav = useNavigate();
    const navigation = useNavigation();
    const isNavigating =
        navigation.state === "submitting" || navigation.state === "loading";
    const isSubmittingRef = useRef(false);
    const submit = useSubmit();
    const location = useLocation();
    const { getItem, removeItem } = useLocalStorage();
    const { isDarkMode } = useTheme();
    const [availableTags, setAvailableTags] = useState([]);

    useEffect(() => {
        axios
            .get("wishlistitem/tags")
            .then((res) => setAvailableTags(res.data));
    }, []);

    const selectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: isDarkMode ? "#212529" : base.backgroundColor,
            borderColor: isDarkMode ? "#495057" : base.borderColor,
            color: isDarkMode ? "white" : base.color,
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: isDarkMode ? "#212529" : base.backgroundColor,
        }),
        option: (base, { isFocused }) => ({
            ...base,
            backgroundColor: isFocused
                ? isDarkMode
                    ? "#495057"
                    : "#deebff"
                : isDarkMode
                ? "#212529"
                : base.backgroundColor,
            color: isDarkMode ? "white" : "black",
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: isDarkMode ? "#495057" : base.backgroundColor,
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: isDarkMode ? "white" : base.color,
        }),
        input: (base) => ({
            ...base,
            color: isDarkMode ? "white" : base.color,
        }),
    };

    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { errors, isDirty },
    } = useForm({
        resolver: yupResolver(WishlistItemDetailSchema),
        defaultValues: request, // Use initial request, not changing record
    });

    useBlocker(() => {
        // Don't block navigation if form is being submitted or already navigating
        if (!isDirty || isNavigating || isSubmittingRef.current) {
            return false;
        }

        if (
            window.confirm(
                "You have unsaved changes. Are you sure you want to leave?"
            )
        ) {
            return false; // Allow navigation
        } else {
            return true; // Block navigation
        }
    });

    useEffect(() => {
        // setup event listener to receive product data from web extension
        const handleWebExProductData = (event) => {
            const { url, title, image } = event.detail;
            handleProductDataReceived({
                product_name: title,
                product_url: url,
                image_url: image?.url,
            });
            document.removeEventListener(
                "receiveProductData",
                handleWebExProductData
            );
        };
        document.addEventListener("receiveProductData", handleWebExProductData);

        // Signal that the page is ready to receive data
        document.dispatchEvent(new CustomEvent("pageReadyForData"));

        return () => {
            document.removeEventListener(
                "receiveProductData",
                handleWebExProductData
            );
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        // Get query param "hash" and store in var
        const params = new URLSearchParams(location.search);
        const hash = params.get("hash");

        // load data from local storage, useful for mobile app share extension
        // ==========================================
        const sharedDataStr = getItem("wishliz-create-detail-temp");
        if (sharedDataStr) {
            try {
                const json = JSON.parse(sharedDataStr);
                if (json.hash && hash && json.hash === hash) {
                    handleProductDataReceived(json);
                }
            } catch (e) {
                console.error(
                    "Failed to parse shared data from local storage",
                    e
                );
            }
            removeItem("wishliz-create-detail-temp");
        }
        // ==========================================

        // load data from cache storage, useful for sharing from service worker (PWA)
        // ==========================================
        var loadData = async () => {
            const keys = await caches.keys();
            const mediaCacheName =
                keys.find((key) => key.startsWith("media")) || "media-cache";
            const cache = await caches.open(mediaCacheName);
            const share_data = await cache.match("/shared-data");
            if (share_data) {
                const json = await share_data.json();
                if (json.hash && hash && json.hash === hash) {
                    handleProductDataReceived(json);
                }
                await cache.delete("/shared-data");
            }
        };

        loadData();
        // ==========================================

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const onSubmit = async (data) => {
        isSubmittingRef.current = true;
        try {
            if (data.id === 0) {
                submit(data, { method: "post", encType: "application/json" });
            } else {
                submit(data, { method: "put", encType: "application/json" });
            }
        } catch (error) {
            isSubmittingRef.current = false;
            throw error;
        }
    };

    const handleProductDataReceived = function ({
        product_name,
        product_url,
        image_url,
    }) {
        const updatedRecord = {
            id: 0,
            product_name: product_name,
            image_url: image_url,
            product_url: product_url,
            tags: [],
        };
        setRecord(updatedRecord);
        reset(updatedRecord); // Reset form with new data
        renderSimpleToast("Page refreshed");
    };

    const onError = (errors, e) => console.error(errors, e);

    return (
        <>
            <Breadcrumb
                activeLabel={
                    record.product_name ? "Edit Product" : "Create Product"
                }
            />
            {actionData?.reason && (
                <Alert variant="danger">{actionData?.reason}</Alert>
            )}
            <Form onSubmit={handleSubmit(onSubmit, onError)} onReset={reset}>
                <div className="d-flex gap-2">
                    <Button
                        variant="primary"
                        hidden={record.id === 0}
                        disabled={isNavigating}
                        title="Back"
                        onClick={() => nav(`/wishlist/${record.id}`)}
                    >
                        <FontAwesomeIcon icon={faCircleLeft} /> Back
                    </Button>
                    <Button
                        variant="success"
                        type="submit"
                        title="Save"
                        disabled={isNavigating}
                    >
                        <FontAwesomeIcon icon={faFloppyDisk} /> Save
                    </Button>
                </div>

                <Row className="mb-3 mt-3">
                    <Form.Group as={Col} md="4" lg="3">
                        <ImageContainer onClick={() => setModalShow(true)}>
                            <ProductImage
                                data-testid="img_product_image"
                                src={
                                    record.image_url
                                        ? record.image_url
                                        : imagePlaceHolder
                                }
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = imagePlaceHolder;
                                }}
                                rounded
                            />
                            {record.id === 0 && !record.image_url && (
                                <ImageOverlay>
                                    <OverlayText>
                                        Click to add image URL
                                        <br />
                                        <small>
                                            System will auto-fill if left empty,
                                            it may take a few minutes
                                        </small>
                                    </OverlayText>
                                </ImageOverlay>
                            )}
                        </ImageContainer>
                    </Form.Group>
                    <Form.Group as={Col} md="8" lg="9">
                        <Controller
                            control={control}
                            name="id"
                            render={({ field: { onChange, value, ref } }) => (
                                <Form.Control
                                    hidden={true}
                                    rules={{ required: true }}
                                    value={value}
                                    isInvalid={errors.id}
                                    onChange={onChange}
                                    data-testid="input_id"
                                    ref={ref}
                                    name="id"
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="product_url"
                            render={({ field: { onChange, value, ref } }) => (
                                <FloatingLabel
                                    className="mb-3"
                                    controlId="product_url"
                                    hidden={record.id !== 0}
                                    label="Product Url"
                                >
                                    <Form.Control
                                        hidden={record.id !== 0}
                                        type="url"
                                        rules={{ required: true }}
                                        isInvalid={errors.product_url}
                                        onChange={onChange}
                                        value={value}
                                        data-testid="input_product_url"
                                        ref={ref}
                                        placeholder="Product Url"
                                        name="product_url"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.product_url?.message}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            )}
                        />

                        <Controller
                            control={control}
                            name="image_url"
                            render={({ field: { onChange, value, ref } }) => (
                                <Form.Control
                                    type="url"
                                    hidden={true}
                                    value={value}
                                    onChange={onChange}
                                    isInvalid={errors.image_url}
                                    ref={ref}
                                    name="image_url"
                                    data-testid="input_image_url"
                                />
                            )}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.image_url?.message}
                        </Form.Control.Feedback>

                        <Controller
                            control={control}
                            name="product_name"
                            render={({ field: { onChange, value, ref } }) => (
                                <FloatingLabel
                                    className="mb-3"
                                    controlId="product_name"
                                    label="Product Name"
                                >
                                    <Form.Control
                                        type="text"
                                        rules={{ required: true }}
                                        isInvalid={errors.product_name}
                                        onChange={onChange}
                                        value={value}
                                        data-testid="input_product_name"
                                        data-is-dirty={isDirty}
                                        ref={ref}
                                        placeholder="Product Name"
                                        name="product_name"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.product_name?.message}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            )}
                        />
                        <Controller
                            control={control}
                            name="tags"
                            render={({ field: { onChange, value } }) => (
                                <CreatableSelect
                                    isMulti
                                    options={availableTags.map((t) => ({
                                        value: t.name,
                                        label: t.name,
                                    }))}
                                    value={(value || []).map((t) => ({
                                        value: t.name,
                                        label: t.name,
                                    }))}
                                    onChange={(selected) =>
                                        onChange(
                                            (selected || []).map((s) => ({
                                                name: s.value,
                                            }))
                                        )
                                    }
                                    placeholder="Add tags..."
                                    styles={selectStyles}
                                />
                            )}
                        />
                    </Form.Group>
                </Row>
            </Form>
            <ImageUrlModal
                image_url={record.image_url}
                modalShow={modalShow}
                onHide={() => setModalShow(false)}
                onImageUrlChange={(image_url) => {
                    const updatedRecord = { ...record };
                    updatedRecord.image_url = image_url;
                    setRecord(updatedRecord);
                    // Update the form field directly to preserve dirty state
                    setValue("image_url", image_url, { shouldDirty: true });
                }}
            />
        </>
    );
};

export default EditDetailForm;
